import queryString from "query-string";
import { ApiAuth, ApiCall, noAuthRetry } from "./api";
import { TPremiseResponse } from "./interface/IDefaults";
import {
  ICompleteRegistrationInputs,
  ICompleteRegistrationResponse,
  IFilterListUser,
  IHistory,
  IHistoryList,
  IInvite,
  IInvitedUserInfoResponse,
  IInviteResponse,
  ILoggedUserUpdateInputs,
  IPaginateHistoryUser,
  IPermissionInput,
  IPermissionResponse,
  IRegistrationInputs,
  IRegistrationResponse,
  IReinviteUserInputs,
  IReinviteUserResponse,
  IResendValidationResponse,
  IUserEditInputs,
  IUserEditResponse,
  IUserForComments,
  IUserInfo,
  IUserUpdateResponse,
  IValidationInputs,
  IValidationResponse,
} from "./interface/user.interface";
import { IBulkResponse } from "./interface/bulk.interface";
import { getCancelToken } from "./cancelToken";

/**
 * Register a new user
 * @param formData Registration data
 * @returns
 */
export const postRegistration = async (
  formData: IRegistrationInputs
): Promise<TPremiseResponse<IRegistrationResponse>> => {
  return await new ApiCall("/users/register").post<IRegistrationResponse, IRegistrationInputs>(formData);
};

/**
 * Validate a user using a 6 digit One-Time-Password
 * @param guid_user ID of the user
 * @param formdata the code
 * @returns
 */
export const postUserValidation = async (
  guid_user: string,
  formdata: IValidationInputs
): Promise<TPremiseResponse<IValidationResponse>> => {
  return await new ApiCall(`/users/validate/${guid_user}`).patch<IValidationResponse, IValidationInputs>(formdata);
};

/**
 * Return the information of the logged user
 * @returns
 */
export const getUserInfo = async (): Promise<TPremiseResponse<IUserInfo>> => {
  return await new ApiCall("/users/info", ApiAuth()).get<IUserInfo>();
};

/**
 * Updates a user
 * @returns
 */
export const postLoggedUserUpdate = async (
  formdata: ILoggedUserUpdateInputs
): Promise<TPremiseResponse<IUserUpdateResponse>> => {
  return await new ApiCall("/users/edit", ApiAuth()).put<IUserUpdateResponse, ILoggedUserUpdateInputs>(formdata);
};

/**
 * Return a basic information of the invited user for the complete registration form
 * @returns
 */
export const getBasicUserInfo = async (guid_user: string): Promise<TPremiseResponse<IInvitedUserInfoResponse>> => {
  return await new ApiCall(`/users/invite-data/${guid_user}`).get<IInvitedUserInfoResponse>();
};

/**
 * GEN-3040: emite um convite NOVO para o usuário e devolve o link de conclusão de cadastro.
 *
 * É POST, e não GET, justamente por não ser idempotente: o backend guarda apenas o hash do token,
 * então o link só existe no instante em que é emitido — e cada emissão invalida a anterior. Por
 * isso a listagem não traz mais o link pronto e esta chamada só acontece quando alguém pede para
 * copiá-lo.
 */
export const postInviteLink = async (guid_user: string): Promise<TPremiseResponse<string>> => {
  // `noAuthRetry` porque esta chamada CRIA entidade (regra do AGENTS.md desta pasta, GEN-3191):
  // se a resposta se perder na volta, o replay emitiria um segundo convite e invalidaria o
  // primeiro — e o admin poderia ficar sem link nenhum enquanto o do e-mail já foi queimado.
  return await new ApiCall(`/users/link-invite/${guid_user}`, noAuthRetry(ApiAuth())).post<string, object>({});
};

/**
 * Send an invitation to a user
 * @returns
 */
export const postSendInvite = async (
  guid_client: string,
  formdata: IInvite
): Promise<TPremiseResponse<IInviteResponse[]>> => {
  return await new ApiCall(`/users/invite/${guid_client}`, ApiAuth()).post<IInviteResponse[], IInvite>(formdata);
};

export const postAllowUserSSO = async (
  guid_client: string,
  formdata: IInvite
): Promise<TPremiseResponse<IInviteResponse[]>> => {
  return await new ApiCall(`/users/allow/sso/${guid_client}`, ApiAuth()).post<IInviteResponse[], IInvite>(formdata);
};

/**
 * Complete the registration of a user that was invited by other user
 * @param hash
 * @param formdata
 * @returns
 */
export const postCompleteRegistration = async (
  hash: string,
  formdata: ICompleteRegistrationInputs
): Promise<TPremiseResponse<ICompleteRegistrationResponse>> => {
  return await new ApiCall(`/users/complete/${hash}`).post<ICompleteRegistrationResponse, ICompleteRegistrationInputs>(
    formdata
  );
};

/**
 * Edit a user
 * @returns
 */
export const putUserEdit = async (
  guid_user: string,
  guid_company: string,
  formdata: IUserEditInputs
): Promise<TPremiseResponse<IUserEditResponse>> => {
  return await new ApiCall(`/users/edit/${guid_company}/${guid_user}`, ApiAuth()).put<
    IUserEditResponse,
    IUserEditInputs
  >(formdata);
};

/**
 * Reinvite User
 * @returns
 */
export const postReinviteUser = async (
  guid_client: string,
  guid_user: string,
  formdata: IReinviteUserInputs
): Promise<TPremiseResponse<IReinviteUserResponse>> => {
  return await new ApiCall(`/users/reinvite/${guid_client}/${guid_user}`, ApiAuth()).post<
    IReinviteUserResponse,
    IReinviteUserInputs
  >(formdata);
};

/**
 * Resend the validation code to the user
 * @returns
 */
export const patchResendMailId = async (guid_user: string): Promise<TPremiseResponse<IResendValidationResponse>> => {
  return await new ApiCall(`/users/resend/id/${guid_user}`).patch();
};

export const patchResendMailMail = async (email: string): Promise<TPremiseResponse<IResendValidationResponse>> => {
  return await new ApiCall(`/users/resend/email`).patch({ email });
};

export const patchPermissions = async (
  guid_client: string,
  guid_user: string,
  formdata: IPermissionInput
): Promise<TPremiseResponse<IPermissionResponse>> => {
  return await new ApiCall(`/permissions/${guid_client}/${guid_user}`, ApiAuth()).patch<
    IPermissionResponse,
    IPermissionInput
  >(formdata);
};

export const getPermissions = async (guid_client: string, guid_user: string): Promise<TPremiseResponse<any>> => {
  return await new ApiCall(`/permissions/${guid_client}/${guid_user}`, ApiAuth()).get();
};

// GEN-2743: canonical permission catalog (single source of truth, served by the backend).
/**
 * GEN-3284 — o catálogo passou a ser sensível ao cliente: a classe "Ações em lote" só vem para
 * cliente destravado pela Taxly, e só com as ações efetivamente destravadas. Sem `guid_client` o
 * backend omite a classe (default fechado), então quem precisa dela TEM que informar o cliente.
 */
export const getPermissionCatalog = async (guid_client?: string): Promise<TPremiseResponse<any>> => {
  const path = guid_client ? `/permissions/catalog?guid_client=${guid_client}` : `/permissions/catalog`;
  return await new ApiCall(path, ApiAuth()).get();
};

// GEN-2743: access profiles (perfis de acesso reutilizáveis).
export const getAccessProfiles = async (guid_client: string): Promise<TPremiseResponse<any>> => {
  return await new ApiCall(`/permissions/profiles/${guid_client}`, ApiAuth()).get();
};

// GEN-2743 (Card B): profiles + assignments (users/groups) for the management table.
export const getAccessProfilesWithAssignments = async (guid_client: string): Promise<TPremiseResponse<any>> => {
  return await new ApiCall(`/permissions/profiles/${guid_client}/assignments`, ApiAuth()).get();
};

export interface IAccessProfilePayload {
  name: string;
  description?: string;
  permissions: string[];
  products?: string[];
  companiesAccess?: string | string[];
  personaAccess?: string | string[];
  // GEN-2722: escopo por tag da Caixa Postal (eCAC). 'all' = sem restrição.
  dteTagsAccess?: string | string[];
  canSeeJurisdictionFederal?: boolean;
  canSeeJurisdictionState?: boolean;
  canSeeJurisdictionMunicipal?: boolean;
}

export const createAccessProfile = async (
  guid_client: string,
  data: IAccessProfilePayload
): Promise<TPremiseResponse<any>> => {
  return await new ApiCall(`/permissions/profiles/${guid_client}`, ApiAuth()).post<any, IAccessProfilePayload>(data);
};

export const updateAccessProfile = async (
  guid_client: string,
  guid_profile: string,
  data: IAccessProfilePayload
): Promise<TPremiseResponse<any>> => {
  return await new ApiCall(`/permissions/profiles/${guid_client}/${guid_profile}`, ApiAuth()).patch<
    any,
    IAccessProfilePayload
  >(data);
};

export const deactivateAccessProfile = async (
  guid_client: string,
  guid_profile: string
): Promise<TPremiseResponse<any>> => {
  return await new ApiCall(`/permissions/profiles/${guid_client}/${guid_profile}`, ApiAuth()).delete<any>();
};

// GEN-2743 (Card B): anexar/desanexar papéis a um usuário ou a um grupo.
export const getUserAccessProfiles = async (guid_client: string, guid_user: string): Promise<TPremiseResponse<any>> => {
  return await new ApiCall(`/permissions/${guid_client}/${guid_user}/profiles`, ApiAuth()).get();
};
export const attachUserAccessProfile = async (
  guid_client: string,
  guid_user: string,
  guid_profile: string
): Promise<TPremiseResponse<any>> => {
  return await new ApiCall(`/permissions/${guid_client}/${guid_user}/profiles`, ApiAuth()).post<
    any,
    { guid_profile: string }
  >({ guid_profile });
};
// GEN-2916 Etapa 3a — atribuição de papel em lote (preview/apply).
export const bulkAttachUserAccessProfile = async (
  guid_client: string,
  guid_profile: string,
  users: string[],
  mode: "preview" | "apply"
): Promise<TPremiseResponse<IBulkResponse>> => {
  return await new ApiCall(`/permissions/${guid_client}/profiles/${guid_profile}/bulk-attach`, ApiAuth()).post<
    IBulkResponse,
    { mode: "preview" | "apply"; users: string[] }
  >({ mode, users });
};
export const detachUserAccessProfile = async (
  guid_client: string,
  guid_user: string,
  guid_profile: string
): Promise<TPremiseResponse<any>> => {
  return await new ApiCall(
    `/permissions/${guid_client}/${guid_user}/profiles/${guid_profile}`,
    ApiAuth()
  ).delete<any>();
};
export const getGroupAccessProfiles = async (
  guid_client: string,
  guid_role: string
): Promise<TPremiseResponse<any>> => {
  return await new ApiCall(`/permissions/roles/${guid_client}/${guid_role}/profiles`, ApiAuth()).get();
};
export const attachGroupAccessProfile = async (
  guid_client: string,
  guid_role: string,
  guid_profile: string
): Promise<TPremiseResponse<any>> => {
  return await new ApiCall(`/permissions/roles/${guid_client}/${guid_role}/profiles`, ApiAuth()).post<
    any,
    { guid_profile: string }
  >({ guid_profile });
};
export const detachGroupAccessProfile = async (
  guid_client: string,
  guid_role: string,
  guid_profile: string
): Promise<TPremiseResponse<any>> => {
  return await new ApiCall(
    `/permissions/roles/${guid_client}/${guid_role}/profiles/${guid_profile}`,
    ApiAuth()
  ).delete<any>();
};
// GEN-2743 (Fase C): permissions a user inherits via papéis/grupos (for read-only "herdado" display).
export const getUserEffectivePermissions = async (
  guid_client: string,
  guid_user: string
): Promise<TPremiseResponse<any>> => {
  return await new ApiCall(`/permissions/${guid_client}/${guid_user}/effective`, ApiAuth()).get();
};

export const getHistory = async (
  guid_client: string,
  guid_user: string,
  pagination: IPaginateHistoryUser
): Promise<TPremiseResponse<IHistoryList>> => {
  return await new ApiCall(`/users/history/${guid_client}/${guid_user}`, ApiAuth()).post<
    IHistoryList,
    IPaginateHistoryUser
  >(pagination);
};

export const getHistoryDescriptionOptions = async (
  guid_client: string,
  guid_user: string,
  search: string
): Promise<TPremiseResponse<any>> => {
  return await new ApiCall(`/users/history-options/${guid_client}/${guid_user}`, ApiAuth()).post<any, any>({ search });
};

export const listUserForComments = async (guid_client: string): Promise<TPremiseResponse<IUserForComments[]>> => {
  return await new ApiCall(`/users/list-for-comments/${guid_client}`, ApiAuth()).post<IUserForComments[], any>();
};

/**
 * List all user
 *
 * @param guid_client
 * @param filters
 */
export const listUsers = async (
  guid_client: string,
  filters: IFilterListUser,
  cancelTokenId = "users.listUsers",
): Promise<TPremiseResponse<any>> => {
  const queryStringParams = queryString.stringify(filters);
  const url = `/users/list/${guid_client}?${queryStringParams}`;
  // GEN-2916 (R12): o id do cancel token é parametrizável porque duas telas podem listar usuários ao
  // mesmo tempo (a listagem e o modal de edição em lote). Com o id compartilhado, o refresh de uma
  // cancelava a busca da outra e a tela cancelada ficava com dado pré-mutação. Sem id informado,
  // o comportamento antigo é preservado.
  return await new ApiCall(url, ApiAuth(), getCancelToken(cancelTokenId)).get<any>();
};

/**
 * List all user
 *
 * @param guid_client
 * @param filters
 */
export const listUsersWithoutPermission = async (
  guid_client: string,
  filters: IFilterListUser,
  cancelTokenId?: string
): Promise<TPremiseResponse<any>> => {
  const queryStringParams = queryString.stringify(filters);
  const url = `/users/list/without-permission/${guid_client}?${queryStringParams}`;
  // GEN-2920: quando o chamador informa um cancelTokenId, chamadas concorrentes com o mesmo id
  // cancelam as anteriores (só a última vale) — usado no Filters p/ deduplicar a rajada disparada
  // na troca de cliente. Sem id, mantém o comportamento antigo (cada call-site independente).
  const cancelToken = cancelTokenId ? getCancelToken(cancelTokenId) : undefined;
  return await new ApiCall(url, ApiAuth(), cancelToken).get<any>();
};
